import Configuration from "../config";
import { log, logWarning } from "./logging";
import { clearUserSession, getSessionUser } from "services/authUtil";
import TalaeraRoutes from "components/routes/TalaeraRoutes";

/* The superagent-use module returns a clone of the superagent provided with the new functionality. */
const agent = require("superagent-use")(require("superagent"));
const prefix = require("superagent-prefix");
const SERVER_BASE_URL = Configuration.SERVER_BASE_URL + "api";
agent.use(prefix(SERVER_BASE_URL));

const SERVER_ERROR_CODE = 5;
const SERVER_ERROR =
  "We were unable to perform your request, and our engineers were notified. In the meantime, please try again.";
const CROSS_DOMAIN_ERROR = SERVER_ERROR;
const OFFLINE_ERROR =
  "Oops, network issues appear to be interfering with your request. Please change networks or try again later";
const AUTH_ERROR =
  "Your session has expired or you are not allowed to perform these actions. Please try to log in again.";

export const SERVER_TIME_OFFSET = "talaera_server_offset";
const USER_ID_HEADER = "x-user-id";
const NA_VALUE = "n/a";

export function getServerBaseUrl() {
  return SERVER_BASE_URL;
}

export interface AdditionalParams {
  returnResponse?: boolean; // return the whole response with headers and other data.
  contentType?: string;
  accept?: string;
  errorResponse?: boolean; // include response to onError
  includeStatusCode?: boolean;
  onUnauthorized?: (res: any) => void;
}

export type OnSuccess<TData = unknown> = (data: TData) => void;
export type OnError = (error: any, res?: any) => void;

function sendRequest<TResponse>(
  request: any,
  onSuccess: OnSuccess<TResponse> = () => {},
  onError: OnError = () => {},
  additionalParams: AdditionalParams = {}
): void {
  request
    .set("Content-Type", additionalParams?.contentType ?? "application/json")
    .set("accept", additionalParams?.accept || "*/*")
    .on("abort", (err: any) => {
      logWarning("Request was aborted", err);
    })
    .end(function (error: any, res: any) {
      if (res?.ok && !error) {
        if (res.headers?.date) {
          const date = new Date(res.headers.date).getTime() - new Date().getTime();
          localStorage.setItem(SERVER_TIME_OFFSET, date.toString());
        }
        if (additionalParams?.returnResponse) {
          onSuccess(res);
        } else {
          onSuccess(res.body);
        }
      } else {
        let errorMsg = error;
        const errorResponse = error?.response;

        if (navigator.onLine === false) {
          errorMsg = OFFLINE_ERROR;
        } else if (errorResponse?.forbidden) {
          const onUnauthorized =
            additionalParams?.onUnauthorized ?? onUnauthorizedDefault;
          onUnauthorized(res);

          if (errorResponse?.body?.errors?.length > 0) {
            errorMsg = "";
            errorResponse.body.errors.forEach((errorObj: any) => {
              errorMsg += errorObj.description + " ";
            });
          } else {
            errorMsg = AUTH_ERROR;
          }
        } else if (
          errorResponse?.body &&
          errorResponse.statusType !== SERVER_ERROR_CODE
        ) {
          errorMsg = errorResponse.body.message ?? errorResponse.body;
        } else if (errorResponse?.statusType === SERVER_ERROR_CODE) {
          //alternative // } else if (res && SERVER_ERROR_CODE === res.statusType) {
          errorMsg = SERVER_ERROR;
        } else if (error?.crossDomain) {
          // https://github.com/visionmedia/superagent/issues/1050#issuecomment-240892386
          // "As far as I know, the XHR interface does not expose existence of
          // preflight checks in any way, and a failed preflight gives us a
          // generic error with status = 0, which we can't distinguish from DNS,
          // TCP connection or offline errors."
          errorMsg = CROSS_DOMAIN_ERROR;
        } else if (error?.message) {
          errorMsg = error.message;
        }

        if (additionalParams?.includeStatusCode) {
          errorMsg = {
            statusCode: error && errorResponse && errorResponse.statusCode,
            errors: errorMsg,
          };
        }

        const method = errorResponse?.req?.method ?? errorResponse?.method ?? "";
        const url = errorResponse?.req?.url ?? errorResponse?.url ?? "";
        log("Error during xhr: " + method + " " + url, {
          err: error,
          res: res ?? NA_VALUE,
          message: JSON.stringify(errorMsg),
          online: navigator.onLine,
        });

        if (additionalParams?.errorResponse) {
          onError(errorMsg, res);
        } else {
          onError(errorMsg);
        }
      }
    });
}

function onUnauthorizedDefault(res: any) {
  // X-User-Id header tells us what user the current session belongs to.
  const serverUserId = res.headers[USER_ID_HEADER];
  const storedUser = getSessionUser();
  if (serverUserId === undefined && storedUser) {
    clearUserSession();
    // FIXME There is no such thing as state in window.location, it's a react-router thing.
    // But we can't use react-router here, so we need to find another way to handle this.
    // window.location.state = {
    //   sessionEpired: true
    // };
    window.location.pathname = TalaeraRoutes.SIGN_IN.path;
  } else if (serverUserId !== storedUser?.id) {
    window.location.pathname = TalaeraRoutes.ROOT.path;
    window.location.reload();
  }
}

export function get(
  path: string,
  query: Record<string, any> | undefined,
  onSuccess?: OnSuccess,
  onError?: OnError
): any {
  const req = agent.get(path).query(query);

  sendRequest(req, onSuccess, onError);
  return req;
}

export function protectedGet<TResponse>(
  path: string,
  query?: any,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError,
  additionalParams?: any
): any {
  const req = agent.get(path).withCredentials().query(query);

  sendRequest(req, onSuccess, onError, additionalParams);
  return req;
}

export function protectedGetBlob<TResponse>(
  path: string,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError
): any {
  const req = agent.get(path).withCredentials().responseType("blob");

  sendRequest(req, onSuccess, onError);
  return req;
}

export function post<TPayload>(
  path: string,
  data: any,
  onSuccess?: OnSuccess<TPayload>,
  onError?: OnError,
  additionalParams?: AdditionalParams
): any {
  const req = agent.post(path).send(data);

  sendRequest(req, onSuccess, onError, additionalParams);
  return req;
}

export function protectedPost<TPayload, TResponse>(
  path: string,
  payload: TPayload,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError,
  additionalParams?: AdditionalParams
): any {
  const req = agent.post(path).withCredentials().send(payload);

  sendRequest(req, onSuccess, onError, additionalParams);
  return req;
}

export function protectedPatch<TPayload, TResponse>(
  path: string,
  payload: TPayload,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError
): any {
  const req = agent.patch(path).withCredentials().send(payload);

  sendRequest(req, onSuccess, onError);
  return req;
}

export function protectedPut<TPayload, TResponse>(
  path: string,
  data: TPayload,
  onSuccess?: OnSuccess<TResponse>,
  onError?: OnError,
  additionalParams?: AdditionalParams
): any {
  const req = agent.put(path).withCredentials().send(data);

  sendRequest(req, onSuccess, onError, additionalParams);
  return req;
}

export function protectedDelete<TResponse>(
  path: string,
  query: string | undefined,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError
): any {
  const req = agent.delete(path).withCredentials().query(query);

  sendRequest(req, onSuccess, onError);
  return req;
}

export function protectedDeleteWithBody<TPayload, TResponse>(
  path: string,
  payload: TPayload,
  onSuccess?: (response: TResponse) => void,
  onError?: OnError
): any {
  const req = agent.delete(path).withCredentials().send(payload);

  sendRequest(req, onSuccess, onError);
  return req;
}

export function getTotalCount(response: any): number {
  return response.total !== undefined ? response.total : response.total_count;
}

// A promisified shim around sendReqest
function apiRequest(request: any, additionalParams?: AdditionalParams): Promise<any> {
  return new Promise((resolve, reject) => {
    request.withCredentials();
    sendRequest(request, resolve, reject, additionalParams);
  });
}

export const api = {
  get: <TResponse>(
    path: string,
    query?: Record<string, any>,
    additionalParams?: AdditionalParams
  ): Promise<TResponse> => {
    const req = agent.get(path).query(query);

    return apiRequest(req, additionalParams);
  },
  post: <TPayload, TResponse>(
    path: string,
    data?: TPayload,
    additionalParams?: AdditionalParams
  ): Promise<TResponse> => {
    const req = agent.post(path).send(data);

    return apiRequest(req, additionalParams);
  },
  put: <TPayload, TResponse>(
    path: string,
    data: TPayload,
    additionalParams?: AdditionalParams
  ): Promise<TResponse> => {
    const req = agent.put(path).send(data);

    return apiRequest(req, additionalParams);
  },
  patch: <TPayload, TResponse>(
    path: string,
    data: TPayload,
    additionalParams?: AdditionalParams
  ): Promise<TResponse> => {
    const req = agent.patch(path).send(data);

    return apiRequest(req, additionalParams);
  },
  delete: <TPayload>(
    path: string,
    data?: TPayload,
    additionalParams?: AdditionalParams
  ): Promise<void> => {
    const req = agent.delete(path).send(data);

    return apiRequest(req, additionalParams);
  },
};
